import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Button, Space, Table, Typography, Tag, message, Descriptions,
  Empty, Spin, Alert, Modal, Statistic, Row, Col, Divider, DatePicker, Popconfirm,
} from 'antd';
import {
  ReloadOutlined, KeyOutlined, CopyOutlined, MessageOutlined, TeamOutlined,
  ExperimentOutlined, CheckCircleOutlined, DownloadOutlined, SaveOutlined, PlayCircleOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import koKR from 'antd/es/date-picker/locale/ko_KR';
import '../styles/Common.css';
import { API_BASE } from '../config/api';

dayjs.locale('ko');

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

// Axios 인스턴스 (토큰 자동 부착)
const api = axios.create({ baseURL: API_BASE });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const ROLE_LABEL = { SOURCE: '야근방(분석 대상)', TARGET: '전달방' };

const parseBreakdown = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const teamColumns = [
  { title: '팀', dataIndex: 'team', key: 'team', width: 160 },
  { title: '인원 수', dataIndex: 'count', key: 'count', width: 90 },
  { title: '인원', dataIndex: 'people', key: 'people',
    render: (people) => (Array.isArray(people) && people.length
      ? people.map((p) => <Tag key={p}>{p}</Tag>) : '-') },
  { title: '근거', dataIndex: 'note', key: 'note', ellipsis: true },
];

const KakaoBotPage = () => {
  const [rooms, setRooms] = useState({ source: null, target: null });
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);

  // 방 등록 모달
  const [regOpen, setRegOpen] = useState(false);
  const [issuing, setIssuing] = useState(null); // 'SOURCE' | 'TARGET' | null
  const [issued, setIssued] = useState({ SOURCE: null, TARGET: null });

  // 테스트 조회/발송
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { status, total, teams, ... }
  const [registering, setRegistering] = useState(false);
  const [running, setRunning] = useState(false);
  const [resultMode, setResultMode] = useState('test'); // 'test' | 'run'

  // 엑셀 내보내기 (기간별)
  const [range, setRange] = useState(null); // [dayjs, dayjs]
  const [exporting, setExporting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [roomsRes, reportsRes] = await Promise.all([
        api.get('/api/kakao/rooms'),
        api.get('/api/kakao/reports'),
      ]);
      setRooms(roomsRes.data || { source: null, target: null });
      setReports(reportsRes.data || []);
    } catch (err) {
      message.error(err.response?.data?.message || '데이터를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const issueCode = async (purpose) => {
    setIssuing(purpose);
    try {
      const { data } = await api.post('/api/kakao/pairing-code', { purpose });
      setIssued((prev) => ({ ...prev, [purpose]: data }));
      message.success('코드를 발급했습니다. 해당 카톡방에 명령을 입력하세요.');
    } catch (err) {
      message.error(err.response?.data?.message || '코드 발급에 실패했습니다.');
    } finally {
      setIssuing(null);
    }
  };

  const exportExcel = async () => {
    const from = range?.[0]?.format('YYYY-MM-DD');
    const to = range?.[1]?.format('YYYY-MM-DD');
    setExporting(true);
    try {
      const res = await api.get('/api/kakao/reports/export', {
        params: { from, to }, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `kakao_meal_${from || 'all'}_${to || 'all'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      message.error('엑셀 내보내기에 실패했습니다.');
    } finally {
      setExporting(false);
    }
  };

  const copy = (textVal) => {
    navigator.clipboard?.writeText(textVal)
      .then(() => message.success('복사되었습니다.'))
      .catch(() => message.warning('복사에 실패했습니다. 직접 선택해 복사하세요.'));
  };

  const deleteReport = async (date) => {
    try {
      const { data } = await api.delete(`/api/kakao/reports/${date}`);
      message.success(data.message || '삭제했습니다.');
      fetchAll();
    } catch (err) {
      message.error(err.response?.data?.message || '삭제에 실패했습니다.');
    }
  };

  const runManual = () => {
    Modal.confirm({
      title: '수동 집계 실행',
      content: '야근방·전달방에 실제로 발송하고 집계에 저장합니다. (이미 발송한 날짜는 다시 보내지 않습니다) 진행할까요?',
      okText: '실행',
      cancelText: '취소',
      onOk: async () => {
        setRunning(true);
        setTestResult(null);
        setResultMode('run');
        try {
          const { data } = await api.post('/api/kakao/run');
          setTestResult(data);
          if (data.status === 'OK') {
            message.success('집계를 실행했습니다.');
            fetchAll();
          } else {
            message.info(data.message || '결과를 확인하세요.');
          }
        } catch (err) {
          const d = err.response?.data;
          setTestResult(d || { status: 'ERROR', message: '요청에 실패했습니다.' });
          message.error(d?.message || '수동 실행에 실패했습니다.');
        } finally {
          setRunning(false);
        }
      },
    });
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    setResultMode('test');
    try {
      const { data } = await api.post('/api/kakao/test-run');
      setTestResult(data);
      if (data.status === 'OK') {
        const sent = (data.groups || []).some((g) => g.posted_target);
        message.success(sent ? '전달방에 발송했습니다.' : '분석 완료(전달방 미등록으로 미발송).');
      } else {
        message.info(data.message || '결과를 확인하세요.');
      }
    } catch (err) {
      const d = err.response?.data;
      setTestResult(d || { status: 'ERROR', message: '요청에 실패했습니다.' });
      message.error(d?.message || '테스트 실행에 실패했습니다.');
    } finally {
      setTesting(false);
    }
  };

  const registerToReports = async () => {
    const groups = testResult?.groups || [];
    if (!groups.length) return;
    setRegistering(true);
    try {
      const { data } = await api.post('/api/kakao/reports/save', { groups });
      message.success(data.message || '집계에 등록했습니다.');
      setTestResult(null);
      fetchAll();
    } catch (err) {
      message.error(err.response?.data?.message || '집계 등록에 실패했습니다.');
    } finally {
      setRegistering(false);
    }
  };

  // ── 현재 방 요약 태그 ──
  const roomTag = (role, room) => (
    <Tag color={room ? 'blue' : 'default'} icon={<TeamOutlined />}>
      {ROLE_LABEL[role].split('(')[0]}: {room ? (room.room_name || room.chat_id) : '미등록'}
    </Tag>
  );

  // ── 모달 내 방 상세 + 코드 발급 ──
  const roomBlock = (role) => {
    const room = role === 'SOURCE' ? rooms.source : rooms.target;
    const code = issued[role];
    return (
      <Card size="small" title={<><TeamOutlined /> {ROLE_LABEL[role]}</>} style={{ marginBottom: 12 }}>
        {room ? (
          <Descriptions column={1} size="small">
            <Descriptions.Item label="방 이름">{room.room_name || '(이름 없음)'}</Descriptions.Item>
            <Descriptions.Item label="chat_id"><Text code>{room.chat_id}</Text></Descriptions.Item>
            <Descriptions.Item label="등록자">{room.registered_by || '-'}</Descriptions.Item>
          </Descriptions>
        ) : (
          <Empty description="아직 등록되지 않음" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: '8px 0' }} />
        )}

        <Divider style={{ margin: '10px 0' }} />

        <Button
          icon={<KeyOutlined />}
          loading={issuing === role}
          onClick={() => issueCode(role)}
          type={room ? 'default' : 'primary'}
          block
        >
          {room ? '재등록 코드 발급' : '등록 코드 발급'}
        </Button>

        {code && (
          <Alert
            style={{ marginTop: 10 }}
            type="success"
            message={
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Text>이 명령을 <b>{ROLE_LABEL[role]}</b>에 입력하세요:</Text>
                <Space wrap>
                  <Text code style={{ fontSize: 16 }}>{code.command}</Text>
                  <Button size="small" icon={<CopyOutlined />} onClick={() => copy(code.command)}>복사</Button>
                </Space>
                <Text type="secondary">만료: {code.expires_at ? new Date(code.expires_at).toLocaleString() : '-'}</Text>
              </Space>
            }
          />
        )}
      </Card>
    );
  };

  // ── 날짜별 집계 표 ──
  const reportColumns = [
    { title: '날짜', dataIndex: 'report_date', key: 'report_date', width: 120,
      render: (v) => (v ? String(v).slice(0, 10) : '-') },
    { title: '구분', dataIndex: 'meal_type', key: 'meal_type', width: 80,
      render: (v) => (v === 'LUNCH'
        ? <Tag color="gold">중식</Tag>
        : <Tag color="geekblue">야근</Tag>) },
    { title: '총 식사', dataIndex: 'total_meals', key: 'total_meals', width: 90,
      render: (v) => <b>{v ?? '-'}</b> },
    { title: '팀 합계', dataIndex: 'sum_of_counts', key: 'sum_of_counts', width: 90 },
    { title: '상태', dataIndex: 'status', key: 'status', width: 90,
      render: (v) => <Tag color={v === 'OK' ? 'green' : v === 'EMPTY' ? 'default' : 'red'}>{v}</Tag> },
    { title: '', key: 'action', width: 60,
      render: (_, record) => (
        <Popconfirm
          title={`${String(record.report_date).slice(0, 10)} 집계를 삭제할까요?`}
          okText="삭제" cancelText="취소" okButtonProps={{ danger: true }}
          onConfirm={() => deleteReport(String(record.report_date).slice(0, 10))}
        >
          <Button size="small" danger type="text" icon={<DeleteOutlined />} />
        </Popconfirm>
      ) },
  ];

  const expandedReport = (record) => {
    const teams = parseBreakdown(record.team_breakdown);
    if (record.status === 'ERROR') {
      return <Alert type="error" showIcon message="집계 오류" description={record.error_message || ''} />;
    }
    if (!teams.length) return <Empty description="팀별 상세 없음" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    return (
      <Table size="small" rowKey={(r, i) => `${record.report_date}-${i}`}
        columns={teamColumns} dataSource={teams} pagination={false} />
    );
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}><MessageOutlined /> 카카오톡 야근 식사 집계</Title>
      <Paragraph type="secondary">
        야근방 하나를 매일 16:00에 분석해 총 식사 수량을 야근방·전달방에 전송합니다.
        아래 <b>테스트</b>는 야근방을 조회해 <b>전달방에만</b> 발송하며 야근방엔 메시지를 남기지 않습니다.
      </Paragraph>

      <Spin spinning={loading}>
        <Card style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <Button type="primary" icon={<KeyOutlined />} onClick={() => setRegOpen(true)}>
              방 등록 / 코드 발급
            </Button>
            <Button icon={<ExperimentOutlined />} loading={testing} onClick={runTest}>
              테스트: 야근방 조회 → 전달방 발송
            </Button>
            <Button danger icon={<PlayCircleOutlined />} loading={running} onClick={runManual}>
              수동 실행 (야근방·전달방 발송 + 저장)
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchAll}>새로고침</Button>
          </Space>
          <div style={{ marginTop: 12 }}>
            <Space wrap>
              {roomTag('SOURCE', rooms.source)}
              {roomTag('TARGET', rooms.target)}
            </Space>
          </div>
        </Card>

        <Card
          title="날짜별 집계 결과 (행 펼치면 팀별 인원)"
          extra={
            <Space wrap>
              <RangePicker locale={koKR} value={range} onChange={setRange} allowClear />
              <Button icon={<DownloadOutlined />} loading={exporting} onClick={exportExcel}>
                엑셀 다운로드
              </Button>
            </Space>
          }
        >
          <Table
            rowKey="report_date"
            columns={reportColumns}
            dataSource={reports}
            expandable={{ expandedRowRender: expandedReport }}
            pagination={{ pageSize: 15 }}
            scroll={{ x: 600 }}
          />
        </Card>
      </Spin>

      {/* 방 등록 / 코드 발급 모달 */}
      <Modal
        title={<><KeyOutlined /> 방 등록 / 코드 발급</>}
        open={regOpen}
        onCancel={() => setRegOpen(false)}
        footer={[
          <Button key="refresh" icon={<ReloadOutlined />} onClick={fetchAll}>방 현황 새로고침</Button>,
          <Button key="close" type="primary" onClick={() => setRegOpen(false)}>닫기</Button>,
        ]}
      >
        <Paragraph type="secondary">
          역할별로 <b>코드 발급</b> 후, 그 코드로 <Text code>@야근방등록_&lt;코드&gt;</Text> /{' '}
          <Text code>@전달방등록_&lt;코드&gt;</Text> 를 해당 카톡방에 입력하면 등록됩니다.
          입력 후 <b>방 현황 새로고침</b>으로 확인하세요.
        </Paragraph>
        {roomBlock('SOURCE')}
        {roomBlock('TARGET')}
      </Modal>

      {/* 실행/테스트 결과 모달 */}
      <Modal
        title={<><ExperimentOutlined /> {resultMode === 'run' ? '수동 실행 결과' : '테스트 조회/발송 결과'}</>}
        open={!!testResult}
        onCancel={() => setTestResult(null)}
        footer={[
          resultMode === 'test' && testResult?.status === 'OK' && (testResult.groups || []).length > 0 ? (
            <Button key="save" type="primary" icon={<SaveOutlined />} loading={registering} onClick={registerToReports}>
              집계에 등록
            </Button>
          ) : null,
          <Button key="close" onClick={() => setTestResult(null)}>닫기</Button>,
        ]}
        width="92vw"
        style={{ maxWidth: 1100, top: 24 }}
        styles={{ body: { maxHeight: '78vh', overflowY: 'auto' } }}
      >
        {testResult && testResult.status === 'OK' && (
          <>
            {!testResult.target_registered && (
              <Alert style={{ marginBottom: 12 }} type="warning" showIcon
                message="전달방이 등록되지 않아 발송하지 않았습니다. 방 등록에서 전달방을 먼저 등록하세요." />
            )}
            {(testResult.groups || []).map((g) => (
              <Card key={g.date} size="small" style={{ marginBottom: 12 }}
                title={
                  <Space>
                    <b>{g.date}({g.weekday})</b>
                    <Tag color={g.meal_type === 'LUNCH' ? 'gold' : 'geekblue'}>{g.meal_label}</Tag>
                  </Space>
                }
                extra={
                  <Space size={4} wrap>
                    {g.already_posted && <Tag>이미 발송됨</Tag>}
                    {g.posted_source && <Tag color="green" icon={<CheckCircleOutlined />}>야근방</Tag>}
                    {g.posted_target
                      ? <Tag color="green" icon={<CheckCircleOutlined />}>전달방</Tag>
                      : <Tag color="orange">전달방 미발송</Tag>}
                  </Space>
                }
              >
                <Row gutter={16} align="middle">
                  <Col><Statistic title="총 식사" value={g.total} suffix="개" /></Col>
                  <Col flex="auto">
                    <Text type="secondary">
                      팀 합계 {g.sum_of_counts ?? '-'}
                      {resultMode === 'test' ? ' · 야근방엔 메시지 안 남김(테스트)' : ''}
                    </Text>
                    <div style={{ marginTop: 6 }}><Text code>{g.kakao_message}</Text></div>
                  </Col>
                </Row>
                <Divider style={{ margin: '10px 0' }} />
                <Table size="small" rowKey={(r, i) => `${g.date}-${i}`} columns={teamColumns}
                  dataSource={g.teams || []} pagination={false} />
              </Card>
            ))}
          </>
        )}
        {testResult && testResult.status !== 'OK' && (
          <Alert
            type={testResult.status === 'ERROR' ? 'error' : 'info'}
            showIcon
            message={testResult.status}
            description={testResult.message || '결과가 없습니다.'}
          />
        )}
      </Modal>
    </div>
  );
};

export default KakaoBotPage;
